import { useSignal } from '@preact/signals';

import type { AgendaReminder } from '@amumin/api-client';
import { t } from '@amumin/i18n';
import { Button, IconPicker } from '@amumin/ui';
import { Avatar, cx, Icon, memberStyle, Pictogram, resolvePicto } from '@amumin/ui-core';

import {
  CHECKLIST_DEFAULT_ICON,
  CHECKLIST_LABEL_MAX,
  CHECKLIST_MAX,
  checklistKey,
  suggestChecklist,
  type ChecklistDraft,
} from '../lib/checklist.js';
import type { MemberLite } from '../lib/agenda.js';
import { hiddenPictoIds, pickerHidden, usePictoCatalog } from '../lib/pictoCatalog.js';
import { suggestPicto } from '../lib/suggest.js';
import { appText, messages } from '../state/session.js';
import styles from './ChecklistEditor.module.css';

// Checklist bij een herinnering (0031, golf 2; founder #7: "Bij het inplannen van een afspraak wil
// ik ook een checklist kunnen toevoegen: Bijvoorbeeld bij zwemles, Onderbroek, Sokken, Handdoek,
// etc. ... meest logische plek om dat weer te geven is bij 'tekst' onder nieuwe herinnering").
// Staat in het herinneringformulier direct onder "Tekst": de lijst (pictogram, woord, omhoog,
// omlaag, weghalen), voorstellen per activiteit als chips met plaatje, en "Iets anders". Het
// pictogram leidt: op de tablet vinkt een kind dat nog niet leest af op het plaatje.

const PICKER_GROUPS = ['packing', 'school', 'sport', 'routine', 'food', 'hobby'] as const;

export function ChecklistEditor({
  value,
  onChange,
  event,
  reminderTitle,
}: {
  value: ReadonlyArray<ChecklistDraft>;
  onChange: (next: ChecklistDraft[]) => void;
  event: { title: string; icon: string | null; category: string | null };
  reminderTitle: string;
}) {
  const a = appText.value;
  const c = a.checklist;
  const m = messages.value;
  const catalog = usePictoCatalog('general');
  const draft = useSignal('');
  const error = useSignal<string | null>(null);
  const picking = useSignal<string | null>(null);
  const items = c.items as Record<string, string>;
  const names = m.design.picto as Record<string, string>;
  const suggestions = suggestChecklist(event, reminderTitle, value).filter((id) => items[id]);
  const full = value.length >= CHECKLIST_MAX;

  const addItems = (ids: ReadonlyArray<string>) => {
    const room = CHECKLIST_MAX - value.length;
    const extra = ids.slice(0, Math.max(0, room)).map((id) => ({
      key: checklistKey(),
      label: items[id] ?? id,
      icon: id,
    }));
    if (extra.length > 0) onChange([...value, ...extra]);
  };

  const addOwn = () => {
    const label = draft.value.trim();
    if (!label) {
      error.value = c.labelRequired;
      return;
    }
    if (label.length > CHECKLIST_LABEL_MAX) {
      error.value = c.labelTooLong;
      return;
    }
    const icon =
      suggestPicto(label, names, [], m.design.pictoKeywords, hiddenPictoIds(catalog)) ??
      CHECKLIST_DEFAULT_ICON;
    onChange([...value, { key: checklistKey(), label, icon }]);
    draft.value = '';
    error.value = null;
  };

  const move = (index: number, by: number) => {
    const next = [...value];
    const [item] = next.splice(index, 1);
    if (!item) return;
    next.splice(index + by, 0, item);
    onChange(next);
  };

  const patch = (key: string, changes: Partial<ChecklistDraft>) =>
    onChange(value.map((x) => (x.key === key ? { ...x, ...changes } : x)));

  return (
    <fieldset class={styles.box} data-name="checklist-editor">
      <legend class={cx('field-label', styles.legend)}>
        <Icon id="list-checks" size={18} /> {c.title}
      </legend>
      <p class="field-hint">{c.hint}</p>

      {value.length > 0 ? (
        <ol class={styles.list} data-name="checklist-list">
          {value.map((item, i) => {
            const picto = resolvePicto(item.icon);
            const pickerOpen = picking.value === item.key;
            return (
              <li key={item.key} class={styles.row} data-name="checklist-item">
                <div class={styles.rowMain}>
                  <button
                    type="button"
                    class={styles.picto}
                    aria-label={t(c.itemPicto, { label: item.label })}
                    aria-expanded={pickerOpen ? 'true' : 'false'}
                    onClick={() => (picking.value = pickerOpen ? null : item.key)}
                    data-name="checklist-picto"
                  >
                    {picto ? (
                      <Pictogram icon={picto} label="" size={36} decorative />
                    ) : (
                      <Icon id="image" size={20} />
                    )}
                  </button>
                  <input
                    class={styles.label}
                    value={item.label}
                    maxLength={CHECKLIST_LABEL_MAX}
                    aria-label={`${c.title} ${i + 1}`}
                    onInput={(e) =>
                      patch(item.key, { label: (e.currentTarget as HTMLInputElement).value })
                    }
                    onBlur={(e) => {
                      const v = (e.currentTarget as HTMLInputElement).value.trim();
                      if (!v) onChange(value.filter((x) => x.key !== item.key));
                    }}
                  />
                  <span class={styles.actions}>
                    <Button
                      name="checklist-up"
                      size="small"
                      variant="ghost"
                      icon="chevron-up"
                      label={t(c.up, { label: item.label })}
                      disabled={i === 0}
                      onPress={() => move(i, -1)}
                    />
                    <Button
                      name="checklist-down"
                      size="small"
                      variant="ghost"
                      icon="chevron-down"
                      label={t(c.down, { label: item.label })}
                      disabled={i === value.length - 1}
                      onPress={() => move(i, 1)}
                    />
                    <Button
                      name="checklist-remove"
                      size="small"
                      variant="ghost"
                      icon="trash-2"
                      label={t(c.remove, { label: item.label })}
                      onPress={() => onChange(value.filter((x) => x.key !== item.key))}
                    />
                  </span>
                </div>
                {pickerOpen ? (
                  <div class={styles.picker}>
                    <IconPicker
                      name={`checklist-picker-${i}`}
                      value={picto}
                      allowNone={false}
                      groups={PICKER_GROUPS}
                      onChange={(next) => {
                        if (next) patch(item.key, { icon: next });
                        picking.value = null;
                      }}
                      hidden={pickerHidden(catalog)}
                      labels={{
                        label: m.design.iconPicker.label,
                        search: m.design.iconPicker.search,
                        none: m.design.iconPicker.none,
                        noResults: m.design.iconPicker.noResults,
                        all: m.design.iconPicker.all,
                        picto: m.design.picto,
                        keywords: m.design.pictoKeywords,
                        category: m.design.pictoCategory,
                      }}
                    />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      ) : (
        <p class={styles.empty}>{c.empty}</p>
      )}

      {full ? (
        <p class="field-hint">{c.max}</p>
      ) : (
        <>
          {suggestions.length > 0 ? (
            <div class={styles.suggest} role="group" aria-label={c.suggestions}>
              {suggestions.map((id) => (
                <button
                  key={id}
                  type="button"
                  class={styles.chip}
                  data-name="checklist-suggestion"
                  data-icon={id}
                  aria-label={t(c.suggestionAdd, { label: items[id] ?? id })}
                  onClick={() => addItems([id])}
                >
                  <Icon id="plus" size={16} />
                  <Pictogram icon={id} label="" size={24} decorative />
                  <span>{items[id]}</span>
                </button>
              ))}
              {suggestions.length > 1 ? (
                <button
                  type="button"
                  class={cx(styles.chip, styles.chipAll)}
                  data-name="checklist-add-all"
                  onClick={() => addItems(suggestions)}
                >
                  <Icon id="list-checks" size={16} />
                  <span>{c.addAll}</span>
                </button>
              ) : null}
            </div>
          ) : null}
          <div class={styles.own}>
            <label class={styles.ownLabel} for="checklist-new">
              {c.newLabel}
            </label>
            <div class={styles.ownRow}>
              <input
                id="checklist-new"
                class={cx(styles.ownInput, error.value && styles.ownInputError)}
                value={draft.value}
                maxLength={CHECKLIST_LABEL_MAX}
                placeholder={c.newPlaceholder}
                aria-invalid={error.value ? 'true' : undefined}
                aria-describedby={error.value ? 'checklist-new-error' : undefined}
                onInput={(e) => {
                  draft.value = (e.currentTarget as HTMLInputElement).value;
                  error.value = null;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addOwn();
                  }
                }}
              />
              <Button name="checklist-add" size="small" icon="plus" onPress={addOwn}>
                {c.add}
              </Button>
            </div>
            {error.value ? (
              <p id="checklist-new-error" class="field-error">
                {error.value}
              </p>
            ) : null}
          </div>
        </>
      )}
    </fieldset>
  );
}

/** Kleine voorvertoning in het voorbeeld van de oproep: de plaatjes naast de kaart. */
export function ChecklistPreview({ list }: { list: ReadonlyArray<ChecklistDraft> }) {
  const c = appText.value.checklist;
  if (list.length === 0) return null;
  return (
    <div class={styles.preview} data-name="checklist-preview">
      <span class={styles.previewTitle}>{c.previewTitle}</span>
      <ul class={styles.previewList}>
        {list.map((i) => (
          <li key={i.key} class={styles.previewItem}>
            <span class={styles.previewBox} aria-hidden="true" />
            <Pictogram icon={i.icon} label="" size={28} decorative />
            <span>{i.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Afspraakblad: per kind wat er al in de tas zit ("Yakub · 4 van 6 ingepakt"), met de plaatjes
 * afgevinkt of nog open. Alleen bij een herinnering met een checklist.
 */
export function ChecklistTicksView({
  reminder,
  members,
}: {
  reminder: AgendaReminder;
  members: ReadonlyArray<MemberLite>;
}) {
  const c = appText.value.checklist;
  const list = reminder.checklist ?? [];
  if (list.length === 0) return null;
  const ticks = new Map((reminder.ticks ?? []).map((x) => [x.member_id, new Set(x.item_ids)]));
  return (
    <div class={styles.ticks} data-name="checklist-ticks">
      <span class={styles.ticksTitle}>
        <Icon id="list-checks" size={16} /> {c.sheetTitle}
      </span>
      <ul class={styles.ticksKids}>
        {reminder.member_ids.map((id) => {
          const kid = members.find((x) => x.id === id);
          if (!kid) return null;
          const done = ticks.get(id) ?? new Set<string>();
          const count = list.filter((i) => done.has(i.id)).length;
          return (
            <li
              key={id}
              class={styles.ticksKid}
              style={memberStyle(kid.color_key)}
              data-name="checklist-kid"
              data-member={id}
            >
              <span class={styles.ticksWho}>
                <Avatar
                  name={kid.display_name}
                  colorKey={kid.color_key}
                  icon={kid.avatar_icon}
                  size="chip"
                  decorative
                />
                <span class={styles.ticksName}>{kid.display_name}</span>
                <span class={styles.ticksCount} data-name="checklist-progress">
                  {count === list.length
                    ? c.sheetAllDone
                    : count === 0
                      ? c.sheetNone
                      : t(c.sheetProgress, { done: count, total: list.length })}
                </span>
              </span>
              <ul class={styles.ticksItems}>
                {list.map((i) => {
                  const on = done.has(i.id);
                  return (
                    <li
                      key={i.id}
                      class={cx(styles.ticksItem, on && styles.ticksItemOn)}
                      title={t(on ? c.sheetItemDone : c.sheetItemOpen, { label: i.label })}
                      aria-label={t(on ? c.sheetItemDone : c.sheetItemOpen, { label: i.label })}
                      data-done={on ? 'true' : 'false'}
                    >
                      <Pictogram icon={i.icon} label="" size={28} decorative />
                      {on ? (
                        <span class={styles.ticksCheck} aria-hidden="true">
                          <Icon id="check" size={12} />
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
